import './condition';

// Import dependencies
import { html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { _x } from '@wordpress/i18n';

// eslint-disable-next-line require-jsdoc
const TemplateActionFilterMixin = ( superClass ) => class extends superClass {
	/**
	 * The logic for evaluating conditions in the filter block.
	 * Acceptable values are `any` or `all`.
	 *
	 * @type {String}
	 * @property {String} logic - The logic of the conditions (any/all).
	 * Attribute name in HTML: `logic`.
	 */
	@property( { type: String } ) accessor logic;

	/**
	 * The list of conditions for the filter block.
	 * Each condition is an object containing information such as integration code and title.
	 *
	 * @type {Array<{integration_code: String, title: String, title_html: String}>}
	 * @property {Array<Object>} conditions - An array of condition objects.
	 * Attribute name in HTML: none (non-reflected property).
	 */
	@property( { type: Array, attribute: false } ) accessor conditions = [];

	/**
	 * The list of items inside the filter block.
	 * Each item represents an action with properties like integration code, title, and delay.
	 *
	 * @type {Array<{integration_code: String, title: String, title_html: String, delay: Object}>}
	 * @property {Array<Object>} items - An array of action item objects.
	 * Attribute name in HTML: none (non-reflected property).
	 */
	@property( { type: Array, attribute: false } ) accessor items = [];

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div class="filter-block">
				${ this._templateHeader }
				${ this._templateItems }
			</div>
		`;
	}

	/**
	 * Creates the header of the filter block
	 * This includes the logic and the conditions
	 *
	 * @return {TemplateResult} The header
	 */
	get _templateHeader() {
		return html`
			<div class="filter-block-header">
				${ this._templateLogic }
				${ this._templateConditions }
			</div>
		`;
	}

	/**
	 * Creates the header that shows the logic of the filter block
	 *
	 * @return {TemplateResult} The logic
	 */
	get _templateLogic() {
		return html`
			<div class="logic">
				<div class="logic__condition-icon">
					<uo-icon id="code-merge"></uo-icon>
				</div>
				
				<div class="logic__condition">${ escapeHTML( _x( 'Run if', 'Conditions logic',  'uncanny-automator' ) ) }</div>

				${
					this.conditions.length > 1 ?
					html`
						<uo-chip>${ this.logic === 'all' ? escapeHTML( _x( 'All', 'Conditions logic', 'uncanny-automator' ) ) : escapeHTML( _x( 'Any', 'Conditions logic', 'uncanny-automator' ) ) }</uo-chip>
						<div class="logic__sentence">${ this.logic === 'all' ? escapeHTML( _x( 'of the following conditions are met', 'Conditions logic', 'uncanny-automator' ) ) : escapeHTML( _x( 'of the following conditions is met', 'Conditions logic', 'uncanny-automator' ) ) }</div>
					` : ''
				}
			</div>
		`;
	}

	/**
	 * Creates the template that shows the conditions
	 *
	 * @return {TemplateResult} The conditions
	 */
	get _templateConditions() {
		return html`
			<div class="conditions">
				${ repeat(
					this.conditions,
					( item, index ) => index,
					condition => html`
						<uap-template-filter-condition
							integration-code=${ condition.integration_code }
							.title=${ condition.title }
							.titleHTML=${ condition.title_html }
						></uap-template-filter-condition>
					`
				) }
			</div>
		`;
	}

	/**
	 * Creates the section that shows all the actions
	 *
	 * @return {TemplateResult} The actions
	 */
	get _templateItems() {
		return html`
			<div class="filter-block-items">
				${ repeat(
					this.items,
					( item, index ) => index,
					item => html`
						<uap-template-item-action 
							integration-code=${ item.integration_code }
							.title=${ item.title }
							.titleHTML=${ item.title_html }
							.delay=${ item.delay }

							?is-pro=${ item.is_pro }
							?is-elite=${ item.is_elite }
						></uap-template-item-action>
					`
				) }
			</div>
		`;
	}
}

export const AutomatorTemplateActionFilterBase = TemplateActionFilterMixin( LitElement );
